import React, { useState, useEffect, useCallback } from 'react';
import { Guide } from '../../types';
import { guideService } from '../../services/guideService';
import GuideForm from './GuideForm';
import { useToast } from '../../contexts/ToastContext';
import { API_BASE_URL } from '../../config/apiConfig';

const API_BASE = API_BASE_URL.replace(/\/api$/, '');

const resolveImg = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
};

type FilterType = 'all' | 'active' | 'inactive';

const GuideManagement: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [activeTab, setActiveTab] = useState<'guides' | 'rankings'>('guides');

  const loadGuides = useCallback(async () => {
    try {
      setLoading(true);
      const all = await guideService.getGuides();
      setGuides(all);
    } catch {
      showError('Failed to load guides', 'Error');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { loadGuides(); }, [loadGuides]);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete guide "${name}"? This cannot be undone.`)) return;
    try {
      await guideService.deleteGuide(id);
      showSuccess('Guide deleted', 'Deleted');
      loadGuides();
    } catch {
      showError('Failed to delete guide', 'Error');
    }
  };

  const handleToggle = async (id: string) => {
    try { await guideService.toggleGuideStatus(id); loadGuides(); }
    catch { showError('Failed to update status', 'Error'); }
  };

  const handleRefreshRatings = async () => {
    try {
      setLoading(true);
      await guideService.updateAllGuideRatings();
      await loadGuides();
      showSuccess('Ratings refreshed', 'Done');
    } catch { showError('Failed to refresh ratings', 'Error'); }
    finally { setLoading(false); }
  };

  const filtered = guides.filter(g => {
    const matchSearch = !search ||
      `${g.firstName} ${g.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      g.email.toLowerCase().includes(search.toLowerCase()) ||
      (g.location || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'active' ? g.active : !g.active);
    return matchSearch && matchFilter;
  });

  const stats = {
    total: guides.length,
    active: guides.filter(g => g.active).length,
    inactive: guides.filter(g => !g.active).length,
    avgRating: guides.length ? (guides.reduce((s, g) => s + (g.rating || 0), 0) / guides.length).toFixed(1) : '0.0',
  };

  if (showForm) {
    return (
      <GuideForm
        guide={editingGuide}
        onClose={() => { setShowForm(false); setEditingGuide(null); }}
        onSubmit={() => { loadGuides(); setShowForm(false); setEditingGuide(null); }}
      />
    );
  }

  const StarRating = ({ rating }: { rating: number }) => {
    const r = Number(rating) || 0;
    return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <svg key={n} className={`w-3.5 h-3.5 ${n <= Math.round(r) ? 'text-amber-400' : 'text-gray-200'}`}
          fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-xs text-gray-500 ml-1">{r.toFixed(1)}</span>
    </div>
  )};

  const GuideCard = ({ guide }: { guide: Guide }) => (
    <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      {/* Card top */}
      <div className="relative bg-slate-900 pt-8 pb-12 px-4 text-center">
        {/* Status badge */}
        <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-bold ${guide.active ? 'bg-green-400/20 text-green-300 border border-green-400/30' : 'bg-red-400/20 text-red-300 border border-red-400/30'}`}>
          {guide.active ? 'Active' : 'Inactive'}
        </div>

        {/* Avatar */}
        <div className="w-24 h-24 rounded-full mx-auto border-4 border-white/30 overflow-hidden shadow-lg bg-primary-700">
          {resolveImg(guide.avatar) ? (
            <img
              src={resolveImg(guide.avatar)}
              alt={`${guide.firstName} ${guide.lastName}`}
              className="w-full h-full object-cover"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-white/60">
              {guide.firstName[0]}{guide.lastName[0]}
            </div>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="px-5 pt-4 pb-5 -mt-6 relative">
        {/* Name card */}
        <div className="bg-white rounded-2xl shadow-md px-4 py-3 mb-4 text-center border border-gray-100">
          <h3 className="font-bold text-gray-900 text-base leading-tight">
            {guide.firstName} {guide.lastName}
          </h3>
          <p className="text-primary-700 text-xs font-medium mt-0.5">{guide.email}</p>
          <div className="mt-2 flex justify-center">
            <StarRating rating={guide.rating} />
          </div>
        </div>

        <div className="space-y-2.5 text-sm mb-4">
          {guide.phone && (
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-base">📞</span>
              <span className="truncate">{guide.phone}</span>
            </div>
          )}
          {guide.location && (
            <div className="flex items-center gap-2 text-gray-600">
              <span className="text-base">📍</span>
              <span className="truncate">{guide.location}</span>
            </div>
          )}
        </div>

        {/* Specialties */}
        {guide.specialties?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {guide.specialties.slice(0, 3).map(s => (
              <span key={s} className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-xs font-medium">{s}</span>
            ))}
            {guide.specialties.length > 3 && (
              <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs">+{guide.specialties.length - 3}</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-gray-100">
          <button
            onClick={() => handleToggle(guide.id)}
            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-colors ${guide.active ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
          >
            {guide.active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => { setEditingGuide(guide); setShowForm(true); }}
            className="p-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDelete(guide.id, `${guide.firstName} ${guide.lastName}`)}
            className="p-2 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const GuideRow = ({ guide }: { guide: Guide }) => (
    <div className="flex gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
      {/* Avatar */}
      <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 bg-primary-100 mt-0.5">
        {resolveImg(guide.avatar) ? (
          <img src={resolveImg(guide.avatar)} alt={`${guide.firstName} ${guide.lastName}`} className="w-full h-full object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-primary-700 font-bold text-sm">
            {guide.firstName[0]}{guide.lastName[0]}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top line: name + status */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="font-bold text-gray-900 text-sm leading-tight">{guide.firstName} {guide.lastName}</span>
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${guide.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {guide.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Email + location */}
        <p className="text-xs text-gray-500 mb-1 truncate">{guide.email}{guide.location && ` · ${guide.location}`}</p>

        {/* Rating + actions */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-50 mt-2">
          <StarRating rating={guide.rating} />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => handleToggle(guide.id)} className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-colors ${guide.active ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
              {guide.active ? 'Deactivate' : 'Activate'}
            </button>
            <button onClick={() => { setEditingGuide(guide); setShowForm(true); }} className="p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            <button onClick={() => handleDelete(guide.id, `${guide.firstName} ${guide.lastName}`)} className="p-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('guides')}
          className={`px-6 py-3 text-sm font-bold transition-all border-b-2 ${activeTab === 'guides' ? 'border-primary-700 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Guides List
        </button>
        <button
          onClick={() => setActiveTab('rankings')}
          className={`px-6 py-3 text-sm font-bold transition-all border-b-2 ${activeTab === 'rankings' ? 'border-primary-700 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Guide Rankings
        </button>
      </div>

      {activeTab === 'guides' ? (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Guides</h2>
              <p className="text-gray-500 text-sm mt-0.5">Manage your expert safari guides</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={handleRefreshRatings}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
              >
                ⭐ Refresh Ratings
              </button>
              <button
                onClick={() => { setEditingGuide(null); setShowForm(true); }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-700 text-white text-sm font-semibold hover:bg-primary-800 transition-colors shadow-md"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Guide
              </button>
            </div>
          </div>

          {/* Stats - Horizontal Scrollable Pills */}
          <div className="flex flex-nowrap-forced gap-3 overflow-x-auto scrollbar-hide pb-1" style={{ flexWrap: 'nowrap' }}>
            {[
              { label: 'Total', value: stats.total, color: 'bg-slate-800 text-white' },
              { label: 'Active', value: stats.active, color: 'bg-emerald-600 text-white' },
              { label: 'Inactive', value: stats.inactive, color: 'bg-slate-500 text-white' },
              { label: 'Avg Rating', value: `${stats.avgRating}★`, color: 'bg-amber-400 text-slate-900' },
            ].map(s => (
              <div key={s.label} className={`${s.color} flex-shrink-0 rounded-2xl px-5 py-3 shadow-sm border border-black/5 flex items-center gap-3`}>
                <p className="text-xs font-bold opacity-70 uppercase tracking-wider">{s.label}</p>
                <p className="text-2xl font-black leading-none">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Search & Filter Bar - Mobile Optimised */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 sm:p-4">
            <div className="flex flex-col gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email, location..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                />
              </div>

              {/* Filter pills + view toggle row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-nowrap-forced gap-2 overflow-x-auto scrollbar-hide" style={{ flexWrap: 'nowrap' }}>
                  {(['all', 'active', 'inactive'] as FilterType[]).map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide capitalize transition-colors flex-shrink-0 ${
                        filter === f ? 'bg-primary-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {f} ({f === 'all' ? stats.total : f === 'active' ? stats.active : stats.inactive})
                    </button>
                  ))}
                </div>

                {/* View mode toggle */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-xl flex-shrink-0">
                  {[
                    { mode: 'grid', icon: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></> },
                    { mode: 'list', icon: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></> },
                  ].map(({ mode, icon }) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode as 'grid' | 'list')}
                      className={`p-2 rounded-lg transition-colors ${viewMode === mode ? 'bg-white shadow-sm text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <svg className="w-4 h-4" fill={mode === 'grid' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke={mode === 'list' ? 'currentColor' : 'none'}>
                        {icon}
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-12 h-12 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-500 text-sm">Loading guides...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
              <div className="text-6xl mb-4">🧭</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No guides found</h3>
              <p className="text-gray-500 text-sm mb-6">
                {search ? 'Try a different search term' : 'Click "Add Guide" to get started'}
              </p>
              {!search && (
                <button
                  onClick={() => { setEditingGuide(null); setShowForm(true); }}
                  className="px-6 py-2.5 bg-primary-700 text-white rounded-xl font-semibold text-sm hover:bg-primary-800 transition-colors"
                >
                  Add First Guide
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map(guide => <GuideCard key={guide.id} guide={guide} />)}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(guide => <GuideRow key={guide.id} guide={guide} />)}
            </div>
          )}
        </>
      ) : (
        <GuideRankingManager />
      )}
    </div>
  );
};

const GuideRankingManager: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const [rankings, setRankings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [newRank, setNewRank] = useState({ name: '', dailyRate: 0, description: '' });

    const loadRankings = async () => {
        setLoading(true);
        try {
            const data = await guideService.getGuideRankings();
            setRankings(data);
        } catch (error) {
            showError('Failed to load rankings', 'Error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadRankings(); }, []);

    const handleAdd = async () => {
        if (!newRank.name) return;
        try {
            await guideService.createGuideRanking({ ...newRank, requirements: [], sortOrder: rankings.length });
            showSuccess('Ranking created', 'Success');
            setIsAdding(false);
            setNewRank({ name: '', dailyRate: 0, description: '' });
            loadRankings();
        } catch {
            showError('Failed to create ranking', 'Error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this ranking?')) return;
        try {
            await guideService.deleteGuideRanking(id);
            showSuccess('Ranking deleted', 'Success');
            loadRankings();
        } catch {
            showError('Failed to delete ranking', 'Error');
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Guide Rankings</h3>
                    <p className="text-sm text-gray-500">Define experience levels and rates for guides</p>
                </div>
                <button
                    onClick={() => setIsAdding(true)}
                    className="px-4 py-2 bg-primary-700 text-white rounded-xl text-sm font-bold shadow-md hover:bg-primary-800 transition-all"
                >
                    + Create New Rank
                </button>
            </div>

            {isAdding && (
                <div className="bg-white p-6 rounded-2xl border-2 border-primary-100 shadow-sm space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rank Name</label>
                            <input
                                value={newRank.name}
                                onChange={e => setNewRank({ ...newRank, name: e.target.value })}
                                className="w-full px-4 py-2 border rounded-xl"
                                placeholder="e.g. Silver Level"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Daily Rate (USD)</label>
                            <input
                                type="number"
                                value={newRank.dailyRate}
                                onChange={e => setNewRank({ ...newRank, dailyRate: Number(e.target.value) })}
                                className="w-full px-4 py-2 border rounded-xl"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                        <textarea
                            value={newRank.description}
                            onChange={e => setNewRank({ ...newRank, description: e.target.value })}
                            className="w-full px-4 py-2 border rounded-xl"
                            rows={2}
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-500 font-bold">Cancel</button>
                        <button onClick={handleAdd} className="px-6 py-2 bg-primary-700 text-white rounded-xl font-bold">Save Ranking</button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full py-20 text-center text-gray-500">Loading rankings...</div>
                ) : rankings.length === 0 ? (
                    <div className="col-span-full py-20 text-center text-gray-500 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                        No rankings created yet.
                    </div>
                ) : (
                    rankings.map(rank => (
                        <div key={rank.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="font-bold text-lg text-primary-900">{rank.name}</h4>
                                <button onClick={() => handleDelete(rank.id)} className="text-red-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">{rank.description}</p>
                            <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Daily Rate</span>
                                <span className="text-xl font-black text-emerald-600">${rank.dailyRate}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const TrashIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

export default GuideManagement;
